import { useState, useEffect } from 'react';
import api from '../../services/api';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { Download, Upload, Archive, AlertTriangle, CheckCircle, RefreshCw, ExternalLink, HardDrive, CloudUpload } from 'lucide-react';
import { format } from 'date-fns';

const BackupManager = ({ user }) => {
  const [historySnapshots, setHistorySnapshots] = useState([]);
  const [physicalBackups, setPhysicalBackups] = useState([]);
  const [weeklyBackups, setWeeklyBackups] = useState([]);
  const [lockedWeeks, setLockedWeeks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);
  const [restoreConfirmation, setRestoreConfirmation] = useState('');
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [filterDate, setFilterDate] = useState('');
  const [filterVehicle, setFilterVehicle] = useState('');
  const [weekStart, setWeekStart] = useState('');
  const [weekEnd, setWeekEnd] = useState('');
  const [autoLockEnabled, setAutoLockEnabled] = useState(true);
  const [storageStats, setStorageStats] = useState(null);
  const [dropboxArchives, setDropboxArchives] = useState([]);
  const [archivingNow, setArchivingNow] = useState(false);
  const [dbxRestore, setDbxRestore] = useState({ open: false, path: '', name: '', confirm: '', busy: false });

  useEffect(() => {
    loadHistory();
    loadPhysicalBackups();
    loadWeeklyBackups();
    loadLockSettings();
    loadWeeklySettings();
    loadStorageStats();
    loadDropboxArchives();
  }, []);

  const loadStorageStats = async () => {
    try {
      const r = await api.get(`${API}/backups/full/storage-stats`);
      setStorageStats(r.data);
    } catch (e) {
      console.error('storage-stats failed', e);
    }
  };

  const loadDropboxArchives = async () => {
    try {
      const r = await api.get(`${API}/backups/full/dropbox-archives`);
      setDropboxArchives(r.data.archives || []);
    } catch (e) {
      console.error('dropbox-archives failed', e);
    }
  };

  const handleArchiveNow = async () => {
    if (!window.confirm('Bundle the current local backups into a single weekly zip and upload to Dropbox?')) return;
    setArchivingNow(true);
    try {
      const r = await api.post(`${API}/backups/full/dropbox-archive-now`);
      toast.success(`Archived: ${r.data.archive.archive_name} (${r.data.archive.size_mb} MB, ${r.data.archive.files_count} files)`);
      await loadDropboxArchives();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Archive failed');
    } finally {
      setArchivingNow(false);
    }
  };

  const handleDropboxRestore = async () => {
    if (dbxRestore.confirm !== 'RESTORE') {
      toast.error('Type RESTORE to confirm');
      return;
    }
    setDbxRestore(prev => ({ ...prev, busy: true }));
    try {
      await api.post(`${API}/backups/full/dropbox-restore`, {
        dropbox_path: dbxRestore.path,
        confirmation: 'RESTORE',
      });
      toast.success(`Restored from ${dbxRestore.name}`);
      setDbxRestore({ open: false, path: '', name: '', confirm: '', busy: false });
      await loadStorageStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Restore failed');
      setDbxRestore(prev => ({ ...prev, busy: false }));
    }
  };

  const loadHistory = async (date = '', vehicleId = '') => {
    try {
      const params = new URLSearchParams();
      if (date) params.append('date', date);
      if (vehicleId) params.append('vehicle_id', vehicleId);
      
      const response = await api.get(`${API}/backups/history?${params}`);
      setHistorySnapshots(response.data.snapshots || []);
    } catch (error) {
      console.error('Error loading history:', error);
      toast.error('Failed to load backup history');
    } finally {
      setLoading(false);
    }
  };

  const loadPhysicalBackups = async () => {
    try {
      const response = await api.get(`${API}/backups/physical`);
      setPhysicalBackups(response.data.backups || []);
    } catch (error) {
      console.error('Error loading physical backups:', error);
      toast.error('Failed to load physical backups');
    }
  };

  const loadWeeklyBackups = async () => {
    try {
      const response = await api.get(`${API}/backups/weekly`);
      setWeeklyBackups(response.data.backups || []);
    } catch (error) {
      console.error('Error loading weekly backups:', error);
      toast.error('Failed to load weekly backups');
    }
  };

  const isWeekLocked = (weekStart, weekEnd) => {
    return lockedWeeks.some(
      week => week.start === weekStart && week.end === weekEnd && week.locked
    );
  };

  const loadLockSettings = async () => {
    try {
      const response = await api.get(`${API}/backups/locks`);
      setLockedWeeks(response.data.locked_weeks || []);
    } catch (error) {
      console.error('Error loading lock settings:', error);
    }
  };

  const loadWeeklySettings = async () => {
    try {
      const response = await api.get(`${API}/backups/weekly/settings`);
      setAutoLockEnabled(response.data.auto_lock_enabled ?? true);
    } catch (error) {
      console.error('Error loading weekly settings:', error);
    }
  };

  const createWeeklyBackup = async () => {
    if (!weekStart || !weekEnd) {
      toast.error('Please select week start and end dates');
      return;
    }

    try {
      toast.info('Creating weekly backup...', { duration: 1500 });
      const response = await api.post(`${API}/backups/weekly/create`, null, {
        params: { 
          week_start: weekStart, 
          week_end: weekEnd,
          auto_lock: autoLockEnabled
        }
      });
      toast.success(response.data.message, { duration: 2500 });
      loadWeeklyBackups();
      loadLockSettings();
      setWeekStart('');
      setWeekEnd('');
    } catch (error) {
      console.error('Error creating weekly backup:', error);
      toast.error(error.response?.data?.detail || 'Failed to create weekly backup');
    }
  };

  const toggleWeekLock = async (weekStart, weekEnd, currentlyLocked) => {
    try {
      const newLockState = !currentlyLocked;
      const response = await api.post(`${API}/backups/locks/toggle`, null, {
        params: {
          week_start: weekStart,
          week_end: weekEnd,
          locked: newLockState
        }
      });
      toast.success(response.data.message);
      loadLockSettings();
    } catch (error) {
      console.error('Error toggling lock:', error);
      toast.error('Failed to toggle lock');
    }
  };

  const handleRestoreHistory = async () => {
    if (restoreConfirmation !== 'RESTORE') {
      toast.error('Please type RESTORE to confirm');
      return;
    }

    try {
      await api.post(`${API}/backups/restore-history/${selectedSnapshot.id}`, {
        confirm: restoreConfirmation
      });
      toast.success('Backup restored successfully! Please refresh the page.');
      setShowRestoreDialog(false);
      setRestoreConfirmation('');
    } catch (error) {
      console.error('Error restoring backup:', error);
      toast.error(error.response?.data?.detail || 'Failed to restore backup');
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Backup & Recovery System</CardTitle>
            <CardDescription>
              Manage backups, view history, and restore previous states
            </CardDescription>
          </div>
          <a
            href="https://app.emergent.sh/workspace/org_e6516b49-11af-4182-89d5-13acdb5725f5/chat"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium text-sm transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            Full System Upgrade
          </a>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="history" className="space-y-4">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="history">History</TabsTrigger>
            <TabsTrigger value="physical">Backup Data</TabsTrigger>
            <TabsTrigger value="weekly">Weekly Backup</TabsTrigger>
            <TabsTrigger value="full-backup" className="text-red-600 font-bold">Full Backup</TabsTrigger>
          </TabsList>

          {/* History Tab */}
          <TabsContent value="history" className="space-y-4">
            <div className="flex flex-col md:flex-row gap-4 mb-4">
              <div className="flex-1">
                <Label>Filter by Date</Label>
                <Input
                  type="date"
                  value={filterDate}
                  onChange={(e) => {
                    setFilterDate(e.target.value);
                    loadHistory(e.target.value, filterVehicle);
                  }}
                  className="mt-1"
                />
              </div>
              <div className="flex-1">
                <Label>Filter by Vehicle ID</Label>
                <Input
                  value={filterVehicle}
                  onChange={(e) => {
                    setFilterVehicle(e.target.value);
                    loadHistory(filterDate, e.target.value);
                  }}
                  placeholder="vehicle_123"
                  className="mt-1"
                />
              </div>
            </div>

            {loading ? (
              <div className="text-center py-8">Loading history...</div>
            ) : historySnapshots.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No history snapshots found. Snapshots are created automatically when you save changes.
              </div>
            ) : (
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {historySnapshots.map((snapshot) => (
                  <div
                    key={snapshot.id}
                    className="p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="font-semibold text-sm">
                          {snapshot.vehicle_id}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          Date: {snapshot.date} | 
                          Change: {snapshot.change_type} | 
                          By: {snapshot.changed_by}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {format(new Date(snapshot.timestamp), 'PPpp')}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => {
                          setSelectedSnapshot(snapshot);
                          setShowRestoreDialog(true);
                        }}
                        variant="outline"
                      >
                        <RefreshCw className="w-3 h-3 mr-1" />
                        Restore
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Physical Backups Tab */}
          <TabsContent value="physical" className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-600">
                Physical backups are stored securely and can be downloaded
              </p>
              <Button onClick={loadPhysicalBackups} variant="outline" size="sm">
                <RefreshCw className="w-4 h-4 mr-1" />
                Refresh
              </Button>
            </div>
            
            {physicalBackups.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No physical backups available
              </div>
            ) : (
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {physicalBackups.map((backup) => (
                  <div
                    key={backup.id}
                    className="p-4 border rounded-lg flex justify-between items-center"
                  >
                    <div>
                      <div className="font-semibold text-sm flex items-center gap-2">
                        <Archive className="w-4 h-4 text-gray-500" />
                        {backup.filename}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Size: {(backup.size / 1024 / 1024).toFixed(2)} MB | 
                        Created: {format(new Date(backup.created_at), 'PPpp')}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => window.open(`${API}/backups/download/${backup.id}`, '_blank')}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Weekly Backup Tab */}
          <TabsContent value="weekly" className="space-y-4">
            <Card className="border-2 border-dashed">
              <CardContent className="pt-4">
                <h4 className="font-semibold mb-3">Create Weekly Backup</h4>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label>Week Start (Monday)</Label>
                    <Input
                      type="date"
                      value={weekStart}
                      onChange={(e) => setWeekStart(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Week End (Friday)</Label>
                    <Input
                      type="date"
                      value={weekEnd}
                      onChange={(e) => setWeekEnd(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-4">
                  <Switch
                    checked={autoLockEnabled}
                    onCheckedChange={setAutoLockEnabled}
                  />
                  <Label>Auto-lock week after backup</Label>
                </div>
                <Button onClick={createWeeklyBackup} className="w-full">
                  <Archive className="w-4 h-4 mr-2" />
                  Create Weekly Backup
                </Button>
              </CardContent>
            </Card>

            {weeklyBackups.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-semibold">Previous Weekly Backups</h4>
                {weeklyBackups.map((backup) => {
                  const locked = isWeekLocked(backup.week_start, backup.week_end);
                  return (
                    <div
                      key={backup.id}
                      className={`p-4 border rounded-lg flex justify-between items-center ${locked ? 'bg-green-50 border-green-200' : ''}`}
                    >
                      <div>
                        <div className="font-semibold text-sm flex items-center gap-2">
                          {locked && <CheckCircle className="w-4 h-4 text-green-600" />}
                          Week: {backup.week_start} to {backup.week_end}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Created: {format(new Date(backup.created_at), 'PPpp')}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant={locked ? "default" : "outline"}
                          onClick={() => toggleWeekLock(backup.week_start, backup.week_end, locked)}
                        >
                          {locked ? 'Unlock' : 'Lock'}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => window.open(`${API}/backups/weekly/download/${backup.id}`, '_blank')}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>
          {/* Full Database Backup Tab */}
          <TabsContent value="full-backup" className="space-y-6">
            {/* Storage Monitor */}
            <div className="border-2 border-slate-300 rounded-lg p-6 bg-slate-50" data-testid="storage-monitor-card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-lg text-slate-700 flex items-center gap-2">
                  <HardDrive className="w-5 h-5" />
                  Storage Monitor
                </h3>
                <Button size="sm" variant="outline" onClick={loadStorageStats} data-testid="refresh-storage-btn">
                  <RefreshCw className="w-3 h-3 mr-1" /> Refresh
                </Button>
              </div>
              {!storageStats ? (
                <p className="text-sm text-slate-500">Loading…</p>
              ) : (
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium text-slate-700">/app partition</span>
                      <span className="text-slate-600" data-testid="disk-usage-text">
                        {storageStats.disk.used_gb} GB / {storageStats.disk.total_gb} GB ({storageStats.disk.used_pct}%)
                      </span>
                    </div>
                    <div className="w-full h-3 rounded-full bg-slate-200 overflow-hidden">
                      <div
                        className={`h-full transition-all duration-500 ${
                          storageStats.disk.used_pct >= 90 ? 'bg-red-500'
                          : storageStats.disk.used_pct >= 75 ? 'bg-amber-500'
                          : 'bg-emerald-500'
                        }`}
                        style={{ width: `${Math.min(storageStats.disk.used_pct, 100)}%` }}
                        data-testid="disk-usage-bar"
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{storageStats.disk.free_gb} GB free</p>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div className="bg-white rounded-lg p-3 border border-slate-200">
                      <p className="text-slate-500 text-xs">Local backups</p>
                      <p className="font-bold text-slate-800 text-lg" data-testid="local-backups-count">{storageStats.local_backups.count} files</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-slate-200">
                      <p className="text-slate-500 text-xs">Disk used by backups</p>
                      <p className="font-bold text-slate-800 text-lg" data-testid="local-backups-size">{storageStats.local_backups.size_mb} MB</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-slate-200">
                      <p className="text-slate-500 text-xs">Retention policy</p>
                      <p className="font-bold text-slate-800 text-lg">Keep latest 7</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Weekly Dropbox Archives */}
            <div className="border-2 border-sky-300 rounded-lg p-6 bg-sky-50" data-testid="dropbox-archives-card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-lg text-sky-700 flex items-center gap-2">
                  <CloudUpload className="w-5 h-5" />
                  Weekly Archives (Dropbox · last month)
                </h3>
                <Button
                  size="sm"
                  onClick={handleArchiveNow}
                  disabled={archivingNow}
                  className="bg-sky-600 hover:bg-sky-700 text-white"
                  data-testid="archive-now-btn"
                >
                  {archivingNow ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <CloudUpload className="w-3 h-3 mr-1" />}
                  Archive current backups now
                </Button>
              </div>
              <p className="text-xs text-sky-700/80 mb-3">
                Every Sunday at 02:00 UK, the local backups are bundled into a single weekly zip and uploaded to Dropbox.
                Up to 4 weeks (≈ 1 month) are shown below. Older archives remain in Dropbox.
              </p>
              {dropboxArchives.length === 0 ? (
                <div className="text-sm text-sky-700 bg-white border border-sky-200 rounded p-3">
                  No weekly archives in Dropbox yet. Click "Archive current backups now" to create the first one.
                </div>
              ) : (
                <div className="space-y-2" data-testid="dropbox-archives-list">
                  {dropboxArchives.map((a) => (
                    <div key={a.dropbox_path} className="flex items-center justify-between bg-white border border-sky-200 rounded p-3">
                      <div>
                        <div className="font-semibold text-sm text-slate-800">{a.archive_name}</div>
                        <div className="text-xs text-slate-500">
                          {a.size_mb} MB
                          {a.created_at && <> · {format(new Date(a.created_at), 'PPp')}</>}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {a.share_url && (
                          <a
                            href={a.share_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-xs px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50"
                            data-testid={`dropbox-link-${a.archive_name}`}
                          >
                            <ExternalLink className="w-3 h-3 mr-1" /> Open in Dropbox
                          </a>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-amber-400 text-amber-700 hover:bg-amber-50"
                          onClick={() => setDbxRestore({ open: true, path: a.dropbox_path, name: a.archive_name, confirm: '', busy: false })}
                          data-testid={`restore-archive-${a.archive_name}`}
                        >
                          <RefreshCw className="w-3 h-3 mr-1" /> Restore
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="border-2 border-red-300 rounded-lg p-6 bg-red-50">
              <h3 className="font-bold text-lg mb-4 text-red-700 flex items-center gap-2">
                <Archive className="w-5 h-5" />
                Full Database Backup
              </h3>
              <p className="text-sm text-red-600 mb-4">
                Download a complete backup of all data in the system. This includes all collections and can be used to restore the entire database.
              </p>
              <Button
                onClick={async () => {
                  try {
                    toast.info('Generating full database backup...', { duration: 3000 });
                    const response = await api.get(`${API}/backup/full-database`, {
                      responseType: 'blob'
                    });
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `full_backup_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.json`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success('Full database backup downloaded!');
                  } catch (error) {
                    console.error('Error downloading backup:', error);
                    toast.error('Failed to download backup');
                  }
                }}
                className="bg-red-600 hover:bg-red-700 text-white gap-2"
              >
                <Download className="w-4 h-4" />
                Download Full Database Backup (JSON)
              </Button>
            </div>

            <div className="border-2 border-purple-300 rounded-lg p-6 bg-purple-50">
              <h3 className="font-bold text-lg mb-4 text-purple-700 flex items-center gap-2">
                <Archive className="w-5 h-5" />
                Quotes & Pro Forma PDF Backup
              </h3>
              <p className="text-sm text-purple-600 mb-4">
                Download all stored PDFs from Quotes and Pro Formas as separate ZIP files.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={async () => {
                    try {
                      toast.info('Generating Quotes PDF backup...', { duration: 3000 });
                      const response = await api.get(`${API}/backup/quotes-pdfs`, {
                        responseType: 'blob'
                      });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `quotes_pdfs_${format(new Date(), 'yyyy-MM-dd')}.zip`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                      toast.success('Quotes PDFs downloaded!');
                    } catch (error) {
                      console.error('Error downloading quotes PDFs:', error);
                      toast.error('Failed to download quotes PDFs');
                    }
                  }}
                  className="bg-purple-600 hover:bg-purple-700 text-white gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download Quotes PDFs (ZIP)
                </Button>
                
                <Button
                  onClick={async () => {
                    try {
                      toast.info('Generating Pro Forma PDF backup...', { duration: 3000 });
                      const response = await api.get(`${API}/backup/proforma-pdfs`, {
                        responseType: 'blob'
                      });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `proforma_pdfs_${format(new Date(), 'yyyy-MM-dd')}.zip`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                      toast.success('Pro Forma PDFs downloaded!');
                    } catch (error) {
                      console.error('Error downloading proforma PDFs:', error);
                      toast.error('Failed to download Pro Forma PDFs');
                    }
                  }}
                  className="bg-purple-600 hover:bg-purple-700 text-white gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download Pro Forma PDFs (ZIP)
                </Button>
              </div>
            </div>

            <div className="border-2 border-orange-300 rounded-lg p-6 bg-orange-50">
              <h3 className="font-bold text-lg mb-4 text-orange-700 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Erase Quotes & Pro Formas Storage
              </h3>
              <p className="text-sm text-orange-600 mb-4">
                Permanently delete all stored quotes and pro forma documents. This action cannot be undone.
              </p>
              <Button
                variant="destructive"
                onClick={async () => {
                  if (!window.confirm('Are you sure you want to delete ALL quotes and pro formas? This action CANNOT be undone.')) return;
                  if (window.prompt('Type "ERASE" to confirm:') !== 'ERASE') {
                    toast.error('Confirmation text did not match');
                    return;
                  }
                  try {
                    const response = await api.delete(`${API}/gdpr/quotes-proformas`);
                    toast.success(response.data.message || 'Documents erased');
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Failed to erase documents');
                  }
                }}
                className="gap-2"
              >
                <AlertTriangle className="w-4 h-4" />
                Erase All Quotes & Pro Formas
              </Button>
            </div>

            {/* Projects Backup Section */}
            <div className="border-2 border-blue-300 rounded-lg p-6 bg-blue-50">
              <h3 className="font-bold text-lg mb-4 text-blue-700 flex items-center gap-2">
                <Archive className="w-5 h-5" />
                Projects Data Backup
              </h3>
              <p className="text-sm text-blue-600 mb-4">
                Backup all projects data before migrating to local server storage.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={async () => {
                    try {
                      toast.info('Generating Projects backup...', { duration: 3000 });
                      const response = await api.get(`${API}/backup/projects`, {
                        responseType: 'blob'
                      });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `projects_backup_${format(new Date(), 'yyyy-MM-dd')}.json`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                      toast.success('Projects backup downloaded!');
                    } catch (error) {
                      console.error('Error downloading projects backup:', error);
                      toast.error('Failed to download projects backup');
                    }
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download Projects Backup (JSON)
                </Button>
                
                <Button
                  variant="destructive"
                  onClick={async () => {
                    if (!window.confirm('Are you sure you want to delete ALL projects data? This action CANNOT be undone.')) return;
                    if (window.prompt('Type "DELETE PROJECTS" to confirm:') !== 'DELETE PROJECTS') {
                      toast.error('Confirmation text did not match');
                      return;
                    }
                    try {
                      const response = await api.delete(`${API}/gdpr/projects`);
                      toast.success(response.data.message || 'Projects data erased');
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Failed to erase projects');
                    }
                  }}
                  className="gap-2"
                >
                  <AlertTriangle className="w-4 h-4" />
                  Erase All Projects Data
                </Button>
              </div>
            </div>

            {/* PI Counts Backup Section */}
            <div className="border-2 border-indigo-300 rounded-lg p-6 bg-indigo-50">
              <h3 className="font-bold text-lg mb-4 text-indigo-700 flex items-center gap-2">
                <Archive className="w-5 h-5" />
                PI Counts Data Backup & Management
              </h3>
              <p className="text-sm text-indigo-600 mb-4">
                Download all PI Counts data or erase corrupted data to fix issues.
              </p>
              <div className="flex flex-wrap gap-3 mb-4">
                <Button
                  onClick={async () => {
                    try {
                      toast.info('Generating PI Counts backup...', { duration: 3000 });
                      const response = await api.get(`${API}/backup/pi-counts`, {
                        responseType: 'blob'
                      });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `pi_counts_backup_${format(new Date(), 'yyyy-MM-dd')}.json`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                      toast.success('PI Counts backup downloaded!');
                    } catch (error) {
                      console.error('Error downloading PI Counts backup:', error);
                      toast.error('Failed to download PI Counts backup');
                    }
                  }}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download PI Counts Backup (JSON)
                </Button>
                
                <Button
                  onClick={async () => {
                    try {
                      toast.info('Generating PI Count PDFs...', { duration: 5000 });
                      const response = await api.get(`${API}/backup/pi-counts-pdfs`, {
                        responseType: 'blob'
                      });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `pi_counts_pdfs_${format(new Date(), 'yyyy-MM-dd')}.zip`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                      toast.success('PI Count PDFs downloaded!');
                    } catch (error) {
                      console.error('Error downloading PI Count PDFs:', error);
                      toast.error('Failed to download PI Count PDFs');
                    }
                  }}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download All PI Count PDFs (ZIP)
                </Button>
              </div>
              
              <div className="border-t border-indigo-200 pt-4 mt-4">
                <h4 className="font-semibold text-indigo-800 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Erase PI Counts Data (Use to fix corrupt data)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Button
                    variant="destructive"
                    onClick={async () => {
                      if (!window.confirm('Delete ALL PI Counts? This cannot be undone.')) return;
                      if (window.prompt('Type "DELETE PI COUNTS" to confirm:') !== 'DELETE PI COUNTS') {
                        toast.error('Confirmation text did not match');
                        return;
                      }
                      try {
                        const response = await api.delete(`${API}/gdpr/pi-counts`);
                        toast.success(response.data.message || 'PI Counts erased');
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed to erase PI Counts');
                      }
                    }}
                    className="gap-2"
                  >
                    <AlertTriangle className="w-4 h-4" />
                    Erase All PI Counts
                  </Button>
                  
                  <Button
                    variant="destructive"
                    onClick={async () => {
                      if (!window.confirm('Delete ALL Discrepancy Reports? This cannot be undone.')) return;
                      if (window.prompt('Type "DELETE DISCREPANCY" to confirm:') !== 'DELETE DISCREPANCY') {
                        toast.error('Confirmation text did not match');
                        return;
                      }
                      try {
                        const response = await api.delete(`${API}/gdpr/discrepancy-reports`);
                        toast.success(response.data.message || 'Discrepancy Reports erased');
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed to erase Discrepancy Reports');
                      }
                    }}
                    className="gap-2"
                  >
                    <AlertTriangle className="w-4 h-4" />
                    Erase Discrepancy Reports
                  </Button>
                  
                  <Button
                    variant="destructive"
                    onClick={async () => {
                      if (!window.confirm('Delete ALL Adjustment Reports? This cannot be undone.')) return;
                      if (window.prompt('Type "DELETE ADJUSTMENTS" to confirm:') !== 'DELETE ADJUSTMENTS') {
                        toast.error('Confirmation text did not match');
                        return;
                      }
                      try {
                        const response = await api.delete(`${API}/gdpr/adjustment-reports`);
                        toast.success(response.data.message || 'Adjustment Reports erased');
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed to erase Adjustment Reports');
                      }
                    }}
                    className="gap-2"
                  >
                    <AlertTriangle className="w-4 h-4" />
                    Erase Adjustment Reports
                  </Button>
                  
                  <Button
                    variant="destructive"
                    onClick={async () => {
                      if (!window.confirm('Delete ALL Stock Valuations? This cannot be undone.')) return;
                      if (window.prompt('Type "DELETE VALUATIONS" to confirm:') !== 'DELETE VALUATIONS') {
                        toast.error('Confirmation text did not match');
                        return;
                      }
                      try {
                        const response = await api.delete(`${API}/gdpr/stock-valuations`);
                        toast.success(response.data.message || 'Stock Valuations erased');
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed to erase Stock Valuations');
                      }
                    }}
                    className="gap-2"
                  >
                    <AlertTriangle className="w-4 h-4" />
                    Erase Stock Valuations
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>

        </Tabs>
      </CardContent>

      {/* Restore Confirmation Dialog */}
      <Dialog open={showRestoreDialog} onOpenChange={setShowRestoreDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Restore Backup
            </DialogTitle>
            <DialogDescription>
              This will restore the selected backup. Current data will be replaced.
            </DialogDescription>
          </DialogHeader>
          
          {selectedSnapshot && (
            <div className="py-4">
              <div className="bg-gray-50 p-3 rounded-lg mb-4">
                <p className="text-sm"><strong>Vehicle:</strong> {selectedSnapshot.vehicle_id}</p>
                <p className="text-sm"><strong>Date:</strong> {selectedSnapshot.date}</p>
                <p className="text-sm"><strong>Backup Time:</strong> {format(new Date(selectedSnapshot.timestamp), 'PPpp')}</p>
              </div>
              
              <Label>Type RESTORE to confirm</Label>
              <Input
                value={restoreConfirmation}
                onChange={(e) => setRestoreConfirmation(e.target.value)}
                placeholder="RESTORE"
                className="mt-1"
              />
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowRestoreDialog(false);
              setRestoreConfirmation('');
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleRestoreHistory}
              className="bg-amber-600 hover:bg-amber-700"
            >
              Restore Backup
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dropbox Archive Restore Dialog */}
      <Dialog open={dbxRestore.open} onOpenChange={(open) => !open && setDbxRestore({ open: false, path: '', name: '', confirm: '', busy: false })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Restore from Dropbox Archive
            </DialogTitle>
            <DialogDescription>
              This will download <strong>{dbxRestore.name}</strong> from Dropbox, extract the most recent
              backup inside, and replace the live database. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Type <span className="font-mono">RESTORE</span> to confirm</Label>
            <Input
              value={dbxRestore.confirm}
              onChange={(e) => setDbxRestore(prev => ({ ...prev, confirm: e.target.value }))}
              placeholder="RESTORE"
              className="mt-1"
              data-testid="dropbox-restore-confirm-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDbxRestore({ open: false, path: '', name: '', confirm: '', busy: false })} disabled={dbxRestore.busy}>
              Cancel
            </Button>
            <Button
              onClick={handleDropboxRestore}
              className="bg-amber-600 hover:bg-amber-700"
              disabled={dbxRestore.busy || dbxRestore.confirm !== 'RESTORE'}
              data-testid="dropbox-restore-confirm-btn"
            >
              {dbxRestore.busy ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
              Restore from Dropbox
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default BackupManager;
